import BusinessIcon from '@mui/icons-material/Business';
import { Box, IconButton, Skeleton, TextField, Typography } from '@mui/material';
import { switchTenant, useTenant } from 'probonio-shared-ui/module/me';
import { mayReadAllTenants } from 'probonio-shared-ui/module/me/permissionSelectors';
import { findTenantInHierarchy } from 'probonio-shared-ui/utils/tenant';
import useDebounce from 'probonio-shared-ui/utils/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BasicDialog, DialogState, useNewDialogState } from '../../component/dialog';
import { DialogLoadingButton } from '../../component/dialog/DialogLoadingButton';
import TenantTree from '../../component/tenantHierarchy/TenantTree';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { resetEmployeeFilter, resetSorting, setTabIndex } from '../userManagement/filter';
import { useAdminTenantAccessData, useTenantSearch } from './TenantHierarchyHooks';

interface Props {
  dialogState?: { dialogState: DialogState; handleOpen: () => void };
  showButton?: boolean;
}

export const TenantSwitcher: React.FC<Props> = ({ dialogState, showButton = true }) => {
  const { tenant, isLoading } = useTenant();
  const { user, adminTenantAccessData: initialAdminTenantAccessData } = useAppSelector(state => state.me);
  const canReadAll = useAppSelector(state => mayReadAllTenants(user));
  const dispatch = useAppDispatch();
  const { t } = useTranslation('navigationModule');
  const internalDialogState = useNewDialogState();
  const selectTenantDialog = dialogState ?? internalDialogState;

  const [inputValue, setInputValue] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState(tenant?.id);
  const debouncedInput = useDebounce(inputValue, 500).trim();
  const { data: adminTenantAccessData } = useAdminTenantAccessData(user, initialAdminTenantAccessData);
  const { data: searchedTenants } = useTenantSearch(debouncedInput, canReadAll);
  const navigate = useNavigate();

  const availableTenantIds = adminTenantAccessData?.availableTenants.map(tenantOption => tenantOption.id) ?? [];
  const prefetchedRootTenants = adminTenantAccessData?.rootTenants ?? [];

  useEffect(() => {
    if (!selectTenantDialog.dialogState.isOpen) {
      return;
    }
    setInputValue('');
    setSelectedTenantId(tenant?.id);
  }, [selectTenantDialog.dialogState.isOpen, tenant?.id]);

  const handleChangeTenant = useCallback(() => {
    if (!selectedTenantId) return;
    navigate('/');
    dispatch(switchTenant({ tenantId: selectedTenantId, isAdmin: true }));
    dispatch(resetSorting());
    dispatch(resetEmployeeFilter());
    dispatch(setTabIndex(0));
  }, [dispatch, navigate, selectedTenantId]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!tenant) {
    return null;
  }

  const isSearchMode = debouncedInput.length > 0;
  const visibleTenantIds = isSearchMode && searchedTenants ? searchedTenants.tenants.map(t => t.id) : availableTenantIds;
  const isSelectedTenantPrefetched =
    !selectedTenantId || prefetchedRootTenants.some(root => !!findTenantInHierarchy(root, selectedTenantId));
  const shouldFetchHierarchies = isSearchMode || !isSelectedTenantPrefetched;

  return (
    <>
      {showButton && (
        <IconButton sx={{ color: 'text.secondary' }} title={t('changeTenantDialog.title')} onClick={selectTenantDialog.handleOpen}>
          <BusinessIcon />
        </IconButton>
      )}
      <BasicDialog
        dialogState={selectTenantDialog.dialogState}
        title={t('changeTenantDialog.title')}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { overflow: 'visible' } }}
        content={
          selectTenantDialog.dialogState.isOpen ? (
            <Box>
              <TextField
                size="small"
                placeholder={t('changeTenantDialog.searchPlaceholder')}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                fullWidth
                autoFocus
                sx={{ marginTop: 0.1, marginBottom: 1 }}
              />
              <Typography mb={2} color="text.secondary">
                {t('changeTenantDialog.text')}
              </Typography>
              <TenantTree
                tenantIds={visibleTenantIds}
                currentTenantId={tenant.id}
                search={debouncedInput}
                rootTenants={prefetchedRootTenants}
                fetchHierarchies={shouldFetchHierarchies}
                selectedTenantId={selectedTenantId}
                setSelectedTenantId={setSelectedTenantId}
              />
            </Box>
          ) : null
        }
        actions={
          <Box>
            <DialogLoadingButton
              loading={isLoading}
              onClick={handleChangeTenant}
              disabled={!selectedTenantId || selectedTenantId === tenant?.id}
            >
              {t('changeTenantDialog.submit')}
            </DialogLoadingButton>
          </Box>
        }
      />
    </>
  );
};
